import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { getContentTypeForExtension } from '@/constants/supported-file-types';
import { backendClient } from '@/lib/api-client';
import { uploadFileWithRetry } from '@/lib/fileUploadUtils';

// Track active uploads in this tab for beforeunload warning
let activeUploads = 0;
export const hasActiveUploads = () => activeUploads > 0;

// Sanitize filename for upload
const sanitizeFilename = (filename: string): string => {
  // Split into basename and extension so sanitization doesn't destroy the extension
  const lastDotIndex = filename.lastIndexOf('.');
  const hasExtension = lastDotIndex > 0;
  const basename = hasExtension ? filename.substring(0, lastDotIndex) : filename;
  const extension = hasExtension ? filename.substring(lastDotIndex) : '';

  const sanitizedBasename = basename
    .replace(/[<>:"|?*]/g, '_')  // Replace invalid characters with underscore
    .replace(/\.\./g, '_')       // Replace double dots in basename
    .replace(/^\.+/, '')         // Remove leading dots
    .trim();

  const sanitizedExtension = extension.replace(/[<>:"|?*]/g, '_');

  return sanitizedBasename + sanitizedExtension;
};

export interface UploadOrchestratorConfig {
  matterGuid: string;
  files: File[];
  documentType?: string | null;
  isDraft?: boolean;
  type?: string;  // Workflow type: 'manual_upload', 'sync', 'bundle'
  source?: string;  // Document origin: 'crimson', 'outlook', 'imanage'
  onProgress?: (update: {
    scannedFiles?: number;
    uploadedFiles?: number;
    rejectedFiles?: string[];
  }) => void;
  onBatchCreated?: () => void | Promise<void>;
}

export interface UploadOrchestratorResult {
  blobNames: string[];
  originalFileNames: string[];
  batchTaskId?: string;
  folderStructure?: Record<string, string>;
  rejectedFiles: string[];
}

type FileWithRelativePath = File & {
  webkitRelativePath?: string;
};

interface BatchFileMapping {
  batch_file_id: number;
  file_name: string;
  folder_path: string | null;
  client_file_id?: string | null;
}

const getFolderPath = (file: FileWithRelativePath): string | null => {
  const relativePath = file.webkitRelativePath;
  if (!relativePath?.includes('/')) {
    return null;
  }

  const pathParts = relativePath.split('/');
  return pathParts.slice(0, -1).join('/');
};

const getFileMappingKey = (fileName: string, folderPath: string | null): string =>
  JSON.stringify([fileName, folderPath ?? null]);

// Chunk size for create/append/SAS API calls. One user drop is still one
// upload batch in the UI; we only split the HTTP payloads.
export const MANUAL_UPLOAD_BACKEND_BATCH_SIZE = 100;
// 80 parallel blob PUTs starved the connection pool so no file was marked
// uploaded before monitor_upload_batches timed the whole drop out. Blob PUTs go
// straight to Azure (a different host from the API). In-flight files refresh
// the stale clock on their own, so a moderate width is enough.
export const MANUAL_UPLOAD_CONCURRENCY = 12;
export const MANUAL_UPLOAD_MARK_FLUSH_SIZE = 10;
// Slow uploads must still report activity before monitor_upload_batches'
// 8-minute stale sweep fails every file that is still uploading.
export const MANUAL_UPLOAD_MARK_FLUSH_INTERVAL_MS = 15_000;
// In-flight PUTs never reach mark-uploaded. Refresh their updated_at inside
// the 8-minute stale window; SAS tokens themselves last an hour.
export const MANUAL_UPLOAD_ACTIVITY_INTERVAL_MS = 60_000;
// ReportUploadActivityRequest rejects more than MAX_UPLOAD_BATCH_FILES (200).
// One oversized body is a 422 and refreshes none of the ids in it.
export const MANUAL_UPLOAD_ACTIVITY_BATCH_SIZE = 200;
// mark-uploaded only touches rows still 'uploading', so re-sending is idempotent.
export const MANUAL_UPLOAD_MARK_MAX_ATTEMPTS = 4;
// After that short burst fails, retry on the flush interval instead of dropping
// the payload. About a minute, not the 8-minute sweep.
export const MANUAL_UPLOAD_MARK_DEFERRED_ATTEMPTS = 4;
const MANUAL_UPLOAD_MARK_RETRY_BASE_DELAY_MS = 300;
// Only for calls the backend makes safe to repeat: create-upload-batch returns the
// existing mappings on replay, appends dedupe by client_file_id, and SAS tokens are
// re-issued while a row is still uploading. Backoff totals 7s, far inside the
// 8-minute stale window.
export const MANUAL_UPLOAD_REQUEST_MAX_ATTEMPTS = 4;
export const MANUAL_UPLOAD_REQUEST_RETRY_BASE_DELAY_MS = 1_000;
const RETRYABLE_REQUEST_STATUSES = new Set([500, 502, 503, 504]);
// Matches MAX_UPLOAD_BATCH_TOTAL_FILES on the backend.
export const MANUAL_UPLOAD_MAX_FILES = 10_000;
// Same text as _validate_manual_upload_destination on the backend.
export const MANUAL_UPLOAD_DESTINATION_REQUIRED_MESSAGE =
  'A destination folder is required when uploading files without folder structure. ' +
  'Select a folder, or use Select Folder to upload a folder hierarchy.';

type MarkStatusUpdate = {
  batch_file_id: number;
  status: 'uploaded' | 'failed';
  error_message?: string;
};

type SasFileInfo = {
  batch_file_id: number;
  filename: string;
  content_type: string;
  file: File;
};

function isRetryableRequestError(error: unknown): boolean {
  if (!axios.isAxiosError(error) || axios.isCancel(error)) {
    return false;
  }
  const status = error.response?.status;
  return status === undefined || RETRYABLE_REQUEST_STATUSES.has(status);
}

async function postWithRetry(url: string, body: unknown) {
  for (let attempt = 1; ; attempt++) {
    try {
      return await backendClient.post(url, body);
    } catch (error) {
      if (attempt >= MANUAL_UPLOAD_REQUEST_MAX_ATTEMPTS || !isRetryableRequestError(error)) {
        throw error;
      }
      console.warn(`Retrying ${url} after attempt ${attempt} failed`, error);
      await new Promise((resolve) =>
        setTimeout(resolve, MANUAL_UPLOAD_REQUEST_RETRY_BASE_DELAY_MS * 2 ** (attempt - 1))
      );
    }
  }
}

// Worker pool: a slot starts the next item as soon as it frees up, so one slow
// file does not hold back the rest. Results keep the input order.
async function processWithConcurrency<T, R>(
  items: T[],
  processor: (item: T, index: number) => Promise<R>,
  concurrency: number
): Promise<R[]> {
  const results: R[] = new Array(items.length);
  let nextIndex = 0;
  const worker = async () => {
    while (nextIndex < items.length) {
      const index = nextIndex++;
      results[index] = await processor(items[index], index);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, worker));
  return results;
}

function mapFilesToBatchIds(
  files: File[],
  clientFileIds: string[],
  fileMappings: BatchFileMapping[],
): Map<number, number> {
  const batchFileIdByClientFileId = new Map<string, number>();
  for (const mapping of fileMappings) {
    if (mapping.client_file_id) {
      batchFileIdByClientFileId.set(mapping.client_file_id, mapping.batch_file_id);
    }
  }

  const requestFileKeys = files.map((file) =>
    getFileMappingKey(file.name, getFolderPath(file as FileWithRelativePath))
  );
  const requestFileKeyCounts = new Map<string, number>();
  for (const key of requestFileKeys) {
    requestFileKeyCounts.set(key, (requestFileKeyCounts.get(key) ?? 0) + 1);
  }

  const mappingKeyCounts = new Map<string, number>();
  const batchFileIdByMappingKey = new Map<string, number>();
  for (const mapping of fileMappings) {
    const key = getFileMappingKey(mapping.file_name, mapping.folder_path);
    mappingKeyCounts.set(key, (mappingKeyCounts.get(key) ?? 0) + 1);
    batchFileIdByMappingKey.set(key, mapping.batch_file_id);
  }

  const batchFileIdByFileIndex = new Map<number, number>();
  for (let index = 0; index < files.length; index++) {
    let batchFileId = batchFileIdByClientFileId.get(clientFileIds[index]);
    const fileKey = requestFileKeys[index];
    if (
      batchFileId === undefined &&
      requestFileKeyCounts.get(fileKey) === 1 &&
      mappingKeyCounts.get(fileKey) === 1
    ) {
      batchFileId = batchFileIdByMappingKey.get(fileKey);
    }

    if (batchFileId !== undefined) {
      batchFileIdByFileIndex.set(index, batchFileId);
    }
  }

  return batchFileIdByFileIndex;
}

function createUploadActivity(batchGuid: string) {
  const openIds = new Set<number>();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  let chain: Promise<void> = Promise.resolve();

  const postOpenIds = () => {
    const ids = [...openIds];
    if (ids.length === 0) {
      return;
    }
    chain = chain.then(async () => {
      for (let offset = 0; offset < ids.length; offset += MANUAL_UPLOAD_ACTIVITY_BATCH_SIZE) {
        const batchFileIds = ids.slice(offset, offset + MANUAL_UPLOAD_ACTIVITY_BATCH_SIZE);
        try {
          await backendClient.post(`/correspondence/batches/${batchGuid}/activity`, {
            batch_file_ids: batchFileIds,
          });
        } catch (error) {
          console.warn('Failed to refresh in-flight upload activity', error);
        }
      }
    });
  };

  const arm = () => {
    if (stopped || timer !== null || openIds.size === 0) {
      return;
    }
    timer = setTimeout(() => {
      timer = null;
      if (stopped || openIds.size === 0) {
        return;
      }
      postOpenIds();
      arm();
    }, MANUAL_UPLOAD_ACTIVITY_INTERVAL_MS);
  };

  return {
    noteStarted(ids: number[]) {
      if (stopped) {
        return;
      }
      for (const id of ids) {
        openIds.add(id);
      }
      arm();
    },
    noteFinished(ids: number[]) {
      for (const id of ids) {
        openIds.delete(id);
      }
      if (openIds.size === 0 && timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    },
    async stop() {
      stopped = true;
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      await chain;
    },
  };
}

function createMarkFlusher(batchGuid: string, onSettled: (ids: number[]) => void) {
  let queue: MarkStatusUpdate[] = [];
  let flushing: Promise<void> = Promise.resolve();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let draining = false;
  // How many times a row was put back after a failed burst. The burst itself
  // is a couple of seconds; these are the later tries.
  const deferredAttempts = new Map<number, number>();
  // batch_file_ids whose 'uploaded' status never reached the backend despite the
  // blob succeeding. The caller surfaces these so the user isn't told the drop
  // succeeded while the sweep quietly fails those files.
  const unconfirmedUploadedIds = new Set<number>();

  const armTimer = () => {
    if (draining || timer !== null) {
      return;
    }
    timer = setTimeout(() => {
      timer = null;
      void send();
    }, MANUAL_UPLOAD_MARK_FLUSH_INTERVAL_MS);
  };

  const postMarks = async (payload: MarkStatusUpdate[]) => {
    if (payload.length === 0) {
      return;
    }
    for (let attempt = 1; attempt <= MANUAL_UPLOAD_MARK_MAX_ATTEMPTS; attempt++) {
      try {
        await backendClient.post(`/correspondence/batches/${batchGuid}/mark-uploaded`, {
          files: payload,
        });
        const confirmedIds = payload.map((entry) => entry.batch_file_id);
        for (const id of confirmedIds) {
          unconfirmedUploadedIds.delete(id);
          deferredAttempts.delete(id);
        }
        onSettled(confirmedIds);
        return;
      } catch (error) {
        if (attempt >= MANUAL_UPLOAD_MARK_MAX_ATTEMPTS) {
          console.warn(
            `Failed to mark ${payload.length} files as uploaded after ${attempt} attempts:`,
            error,
          );
          const retryable: MarkStatusUpdate[] = [];
          const abandonedIds: number[] = [];
          for (const entry of payload) {
            const alreadyDeferred = deferredAttempts.get(entry.batch_file_id) ?? 0;
            if (alreadyDeferred < MANUAL_UPLOAD_MARK_DEFERRED_ATTEMPTS) {
              deferredAttempts.set(entry.batch_file_id, alreadyDeferred + 1);
              retryable.push(entry);
            } else {
              if (entry.status === 'uploaded') {
                unconfirmedUploadedIds.add(entry.batch_file_id);
              }
              abandonedIds.push(entry.batch_file_id);
            }
          }
          if (retryable.length > 0) {
            queue = [...retryable, ...queue];
            armTimer();
          }
          if (abandonedIds.length > 0) {
            onSettled(abandonedIds);
          }
          return;
        }
        await new Promise((resolve) =>
          setTimeout(resolve, attempt * MANUAL_UPLOAD_MARK_RETRY_BASE_DELAY_MS)
        );
      }
    }
  };

  const send = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
    const payload = queue;
    queue = [];
    flushing = flushing.then(() => postMarks(payload));
    return flushing;
  };

  return {
    enqueue(entry: MarkStatusUpdate) {
      queue.push(entry);
      if (queue.length >= MANUAL_UPLOAD_MARK_FLUSH_SIZE) {
        void send();
      } else {
        armTimer();
      }
    },
    async flush() {
      draining = true;
      try {
        if (timer !== null) {
          clearTimeout(timer);
          timer = null;
        }
        if (queue.length > 0) {
          await send();
        } else {
          await flushing;
        }
        while (queue.length > 0) {
          await new Promise((resolve) => setTimeout(resolve, MANUAL_UPLOAD_MARK_FLUSH_INTERVAL_MS));
          await send();
        }
      } finally {
        draining = false;
      }
    },
    unconfirmedUploadedIds,
  };
}

/**
 * Upload orchestrator that handles Azure upload and backend queueing.
 * Progress is tracked in the database and polled by UploadProgressCenter
 *
 * Architecture:
 * 1. Create one upload batch with the full file count, then append remaining files
 * 2. Notify the UI only after every file is registered
 * 3. Issue SAS tokens and upload to blob storage with bounded concurrency
 * 4. mark-uploaded every few files or seconds, and refresh updated_at while a PUT is still open, so the 8-minute stale sweep sees activity
 * 5. Backend monitoring job auto-triggers processing when the expected files are uploaded
 */
export async function orchestrateUpload(config: UploadOrchestratorConfig): Promise<UploadOrchestratorResult> {
  const {
    matterGuid,
    files,
    documentType,
    isDraft = false,
    type = 'manual_upload',
    source = 'crimson',
    onBatchCreated
  } = config;

  if (files.length > MANUAL_UPLOAD_MAX_FILES) {
    throw new Error(
      `You can upload at most ${MANUAL_UPLOAD_MAX_FILES.toLocaleString()} files at once. Please split the upload into smaller batches.`
    );
  }

  const allBlobNames: string[] = [];
  const allOriginalFileNames: string[] = [];
  const folderStructure: Record<string, string> = {};
  let hasFolderStructure = false;
  const rejectedFiles: string[] = [];

  const folderId = documentType ? parseInt(documentType, 10) : null;

  // Mirrors the backend's destination check, which only sees the chunk it receives. A loose
  // file in a later chunk would otherwise fail its append after earlier chunks registered.
  if (
    type === 'manual_upload' &&
    source === 'crimson' &&
    !folderId &&
    files.some((file) => !getFolderPath(file as FileWithRelativePath)?.trim())
  ) {
    throw new Error(MANUAL_UPLOAD_DESTINATION_REQUIRED_MESSAGE);
  }

  const batchGuid = uuidv4();
  const clientFileIds = files.map(() => uuidv4());
  const fileMappings: BatchFileMapping[] = [];
  const activity = createUploadActivity(batchGuid);
  const marks = createMarkFlusher(batchGuid, (ids) => activity.noteFinished(ids));
  let batchFileIdByFileIndex = new Map<number, number>();

  activeUploads++;

  try {
    for (let offset = 0; offset < files.length; offset += MANUAL_UPLOAD_BACKEND_BATCH_SIZE) {
      const chunk = files.slice(offset, offset + MANUAL_UPLOAD_BACKEND_BATCH_SIZE);
      const chunkClientIds = clientFileIds.slice(offset, offset + MANUAL_UPLOAD_BACKEND_BATCH_SIZE);
      const batchFiles = chunk.map((file, index) => ({
        file_name: file.name,
        folder_path: getFolderPath(file as FileWithRelativePath),
        client_file_id: chunkClientIds[index],
      }));

      const registerResponse = offset === 0
        ? await postWithRetry(`/correspondence/${matterGuid}/create-upload-batch`, {
            batch_guid: batchGuid,
            files: batchFiles,
            expected_file_count: files.length,
            type,
            source,
            folder_id: folderId,
            is_draft: isDraft,
          })
        : await postWithRetry(`/correspondence/${matterGuid}/batches/${batchGuid}/files`, {
            files: batchFiles,
          });

      fileMappings.push(...registerResponse.data.file_mappings);
    }

    if (onBatchCreated) {
      try {
        await onBatchCreated();
      } catch (_error) {
        // Ignore errors from callback
      }
    }

    batchFileIdByFileIndex = mapFilesToBatchIds(files, clientFileIds, fileMappings);

    const filesWithoutIds: string[] = [];
    for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
      if (batchFileIdByFileIndex.get(fileIndex) === undefined) {
        const file = files[fileIndex];
        const relativePath = (file as FileWithRelativePath).webkitRelativePath;
        filesWithoutIds.push(relativePath || file.name);
      }
    }

    if (filesWithoutIds.length > 0) {
      console.error('Batch file ID lookup failed for files:', filesWithoutIds);
      console.error('File mappings received:', fileMappings);
      throw new Error(`Failed to map ${filesWithoutIds.length} file(s) to batch. Please try uploading again.`);
    }

    for (let offset = 0; offset < files.length; offset += MANUAL_UPLOAD_BACKEND_BATCH_SIZE) {
      const chunk = files.slice(offset, offset + MANUAL_UPLOAD_BACKEND_BATCH_SIZE);
      const sasFileInfos: SasFileInfo[] = [];
      for (const [index, file] of chunk.entries()) {
        const batchFileId = batchFileIdByFileIndex.get(offset + index);
        if (batchFileId === undefined) {
          continue;
        }
        const sanitizedFilename = sanitizeFilename(file.name);
        const file_extension = file.name.toLowerCase().split('.').pop() || '';
        const content_type = file.type || getContentTypeForExtension(file_extension);
        sasFileInfos.push({
          batch_file_id: batchFileId,
          filename: encodeURIComponent(sanitizedFilename),
          content_type: encodeURIComponent(content_type),
          file,
        });
      }

      if (sasFileInfos.length === 0) {
        continue;
      }

      const sasResponse = await postWithRetry(`/correspondence/${matterGuid}/generate-upload-sas-tokens`, {
        batch_guid: batchGuid,
        files: sasFileInfos.map(({ batch_file_id, filename, content_type }) => ({
          batch_file_id,
          filename,
          content_type,
        })),
      });

      const tokenByBatchFileId = new Map<number, { upload_url: string; blob_name: string }>();
      for (const token of sasResponse.data.tokens) {
        tokenByBatchFileId.set(token.batch_file_id, token);
      }

      // The SAS call stamped updated_at once. Until mark-uploaded, these rows
      // are still `uploading` and a long PUT would otherwise go quiet.
      activity.noteStarted(sasFileInfos.map((info) => info.batch_file_id));

      const chunkResults = await processWithConcurrency(
        sasFileInfos,
        async ({ batch_file_id, file }) => {
          const tokenInfo = tokenByBatchFileId.get(batch_file_id);
          if (!tokenInfo) {
            console.error(`No SAS token returned for batch_file_id ${batch_file_id} (${file.name})`);
            rejectedFiles.push(file.name);
            marks.enqueue({
              batch_file_id,
              status: 'failed',
              error_message: 'No SAS token returned',
            });
            return { blobName: '', fileName: file.name };
          }

          try {
            await uploadFileWithRetry(tokenInfo.upload_url, file, file.name, 3, 120000);
          } catch (error) {
            const message = error instanceof Error ? error.message : 'Upload failed';
            console.error(`Failed to upload ${file.name}:`, error);
            rejectedFiles.push(file.name);
            marks.enqueue({
              batch_file_id,
              status: 'failed',
              error_message: message,
            });
            return { blobName: '', fileName: file.name };
          }

          const relativePath = (file as FileWithRelativePath).webkitRelativePath;
          if (relativePath?.includes('/')) {
            const pathParts = relativePath.split('/');
            folderStructure[tokenInfo.blob_name] = pathParts.slice(0, -1).join('/');
            hasFolderStructure = true;
          }

          marks.enqueue({ batch_file_id, status: 'uploaded' });
          return {
            blobName: tokenInfo.blob_name,
            fileName: file.name,
          };
        },
        MANUAL_UPLOAD_CONCURRENCY
      );

      for (const result of chunkResults) {
        if (result.blobName) {
          allBlobNames.push(result.blobName);
          allOriginalFileNames.push(result.fileName);
        }
      }
    }

    if (allBlobNames.length === 0) {
      return {
        blobNames: [],
        originalFileNames: [],
        batchTaskId: batchGuid,
        rejectedFiles,
      };
    }

    return {
      blobNames: allBlobNames,
      originalFileNames: allOriginalFileNames,
      batchTaskId: batchGuid,
      folderStructure: hasFolderStructure ? folderStructure : undefined,
      rejectedFiles,
    };
  } finally {
    await marks.flush();
    await activity.stop();
    // Blobs whose 'uploaded' status never landed will be failed by the stale
    // sweep, so report them as rejected rather than letting the drop look clean.
    if (marks.unconfirmedUploadedIds.size > 0) {
      const fileNameByBatchFileId = new Map<number, string>();
      for (const [fileIndex, batchFileId] of batchFileIdByFileIndex) {
        fileNameByBatchFileId.set(batchFileId, files[fileIndex].name);
      }
      for (const batchFileId of marks.unconfirmedUploadedIds) {
        const fileName = fileNameByBatchFileId.get(batchFileId);
        if (fileName && !rejectedFiles.includes(fileName)) {
          rejectedFiles.push(fileName);
        }
      }
    }
    activeUploads = Math.max(0, activeUploads - 1);
  }
}
